import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { toReadableName } from '@/app/(app)/analytics/call-analytics/[id]/components/call-metrics-card';
import {
  BooleanMetricStatus,
  buildMetricStatus,
  ListMetricStatus,
  NumberMetricStatus,
  StringMetricStatus,
} from '@/app/(app)/analytics/call-analytics/components/metric-status';
import { Select } from '@/components/atoms/select';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { useMetricsRegistry } from '../../provider';

type MetricValue = ApiSchemas['ExternalCallMetric'];

export interface MetricOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callId: string;
  metric: MetricValue;
  metricKey: string;
}

interface MetricValueProps {
  metricStatus: ReturnType<typeof buildMetricStatus>;
  schema?: ApiSchemas['MetricsRegistry'];
}

interface MetricInputProps {
  metricStatus: ReturnType<typeof buildMetricStatus>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  schema?: ApiSchemas['MetricsRegistry'];
  metric: ApiSchemas['ExternalCallMetric'];
  disabled: boolean;
}

export function MetricOverrideDialog({ open, onOpenChange, callId, metric, metricKey }: MetricOverrideDialogProps) {
  const [reason, setReason] = useState('');
  const { api } = useApiV1Ctx();
  const { metricsById } = useMetricsRegistry();

  const schema = metricsById.get(metricKey);

  const { mutate, isPending } =
    api.externalCalls.overrideCallMetricsExternalCallsExternalCallIdMetricsPatch.useMutation(
      { path: { external_call_id: callId } },
      {
        onSuccess: () => {
          toast.success('Metric override saved');
          onOpenChange(false);
          // Invalidate the call query to refetch updated data
          api.externalCalls.getByIdExternalCallsExternalCallIdGet.invalidateQueries();
        },
        onError: () => {
          toast.error('Failed to save metric override');
        },
      }
    );

  const readableName = useMemo(() => {
    return toReadableName(metric.label || schema?.label || '');
  }, [metric.label, schema?.label]);

  const metricStatus = useMemo(() => {
    return buildMetricStatus(metric);
  }, [metric]);

  const initialValue = useMemo(() => {
    if (!metricStatus || metricStatus.isEmpty) return '';

    switch (metricStatus.kind) {
      case 'number':
        return (metricStatus as NumberMetricStatus).value ?? 0;
      case 'boolean':
        return (metricStatus as BooleanMetricStatus).truthy ?? false;
      case 'list':
        return (metricStatus as ListMetricStatus).value ?? [];
      case 'string':
        return (metricStatus as StringMetricStatus).value ?? '';
      default:
        return '';
    }
  }, [metricStatus]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [newValue, setNewValue] = useState<any>(initialValue);

  useEffect(() => {
    if (open) {
      setNewValue(initialValue);
      setReason(metric.override_reason || '');
    }
  }, [open, initialValue, metric.override_reason]);

  const onSubmit = () => {
    if (newValue === null || newValue === undefined || newValue === '') {
      toast.error('New value is required');
      return;
    }

    let metricValue: ApiSchemas['MetricValue'];
    if (!metricStatus || metricStatus.isEmpty) {
      metricValue = { type: 'string', s: String(newValue) };
    } else {
      switch (metricStatus.kind) {
        case 'boolean':
          metricValue = { type: 'boolean', b: Boolean(newValue) };
          break;
        case 'number':
          metricValue = { type: 'number', n: Number(newValue) };
          break;
        case 'list':
          metricValue = { type: 'list_of_strings', ss: Array.isArray(newValue) ? newValue : [] };
          break;
        case 'string':
        default:
          metricValue = { type: 'string', s: String(newValue) };
          break;
      }
    }

    const updatedMetric: ApiSchemas['ExternalCallMetric'] = {
      ...metric,
      value: metricValue,
      override: true,
      override_reason: reason,
    };

    mutate({
      [metricKey]: updatedMetric,
    });
  };

  const renderOriginalValue = () => {
    if (!metricStatus || metricStatus.isEmpty) {
      return <span className='text-sm text-muted-foreground'>—</span>;
    }

    return <MetricValue metricStatus={metricStatus} schema={schema} />;
  };

  const renderInput = () => {
    if (!metricStatus || metricStatus.isEmpty) {
      return (
        <div className='flex h-8 items-center'>
          <Input
            id='new-value'
            type='text'
            value={newValue || ''}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder='Enter new value'
            disabled={isPending}
          />
        </div>
      );
    }

    return (
      <div className='flex h-8 items-center'>
        <MetricInput
          metricStatus={metricStatus}
          value={newValue}
          onChange={setNewValue}
          schema={schema}
          metric={metric}
          disabled={isPending}
        />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{readableName}</DialogTitle>
          {schema?.description && <DialogDescription className='text-xs'>{schema.description}</DialogDescription>}
        </DialogHeader>
        <div className='grid gap-4 py-4'>
          <div className='grid grid-cols-4 items-start gap-2'>
            <Label className='text-right text-muted-foreground'>
              {metric?.override ? 'Current Score' : 'AI Score'}
            </Label>
            <div className='col-span-3 space-y-2'>
              <div className='flex items-center gap-2'>
                {renderOriginalValue()}
                {!metric?.override && metric?.confidence && (
                  <div
                    className='inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground'
                    title='AI Confidence'
                  >
                    Confidence: {Math.round(metric.confidence * 100)}%
                  </div>
                )}
              </div>
              {!metric?.override && metric?.justify && (
                <p className='text-xs text-muted-foreground'>{metric.justify}</p>
              )}
            </div>
          </div>
          <div className='col-span-4 border-t' />
          <div className='grid grid-cols-4 items-start gap-2'>
            <div className='flex h-8 items-center'>
              <Label htmlFor='new-value' className='text-right'>
                Your Score
              </Label>
            </div>
            <div className='col-span-3 space-y-3'>
              {renderInput()}
              <div className='space-y-1'>
                <Textarea
                  id='reason'
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder='Reason for override (optional)'
                  disabled={isPending}
                  rows={3}
                  className='resize-none'
                />
                <p className='text-xs text-muted-foreground'>Your feedback helps improve AI accuracy</p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline' disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={onSubmit} disabled={isPending || newValue === initialValue}>
            {isPending ? 'Overriding...' : 'Override'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetricValue({ metricStatus, schema }: MetricValueProps) {
  if (!metricStatus) {
    return <span className='text-sm text-muted-foreground'>—</span>;
  }

  switch (metricStatus.kind) {
    case 'number': {
      const numStatus = metricStatus as NumberMetricStatus;
      const value = Math.round(numStatus.value ?? 0);
      const valueDescriptions = schema?.values as Record<string, string> | undefined;

      if (valueDescriptions && valueDescriptions[String(value)]) {
        return <span className='text-sm font-medium'>{valueDescriptions[String(value)]}</span>;
      }

      return (
        <span className='text-sm font-medium'>
          {value}/{numStatus.max}
        </span>
      );
    }
    case 'boolean': {
      const boolStatus = metricStatus as BooleanMetricStatus;
      return <span className='text-sm font-medium'>{boolStatus.truthy ? 'Yes' : 'No'}</span>;
    }
    case 'string': {
      const stringStatus = metricStatus as StringMetricStatus;
      return <span className='text-sm font-medium'>{stringStatus.value || '—'}</span>;
    }
    case 'list': {
      const listStatus = metricStatus as ListMetricStatus;
      const list = listStatus.value;
      if (list.length === 0) {
        return <span className='text-sm text-muted-foreground'>None</span>;
      }
      return <span className='text-sm font-medium'>{list.join(', ')}</span>;
    }
    default:
      return <span className='text-sm text-muted-foreground'>—</span>;
  }
}

function MetricInput({ metricStatus, value, onChange, schema, metric, disabled }: MetricInputProps) {
  if (!metricStatus) {
    return null;
  }

  switch (metricStatus.kind) {
    case 'number': {
      const valueDescriptions = schema?.values as Record<string, string> | undefined;

      if (valueDescriptions) {
        const options = Object.entries(valueDescriptions).map(([val, description]) => ({
          value: val,
          label: description,
        }));

        return (
          <Select
            value={String(value ?? '')}
            onChange={(val) => onChange(Number(val))}
            options={options}
            placeholder='Select a value'
            disabled={disabled}
            className='w-full'
          />
        );
      }

      return (
        <div className='space-y-1'>
          <Input
            id='new-value'
            type='number'
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
            placeholder='Enter number'
            disabled={disabled}
            min={schema?.min ?? metric.min ?? undefined}
            max={schema?.max ?? metric.max ?? undefined}
          />
          {(schema?.min != null || schema?.max != null) && (
            <p className='text-xs text-muted-foreground'>
              {schema.min != null && schema.max != null
                ? `Range: ${schema.min} to ${schema.max}`
                : schema.min != null
                  ? `Minimum: ${schema.min}`
                  : `Maximum: ${schema.max}`}
            </p>
          )}
        </div>
      );
    }
    case 'boolean': {
      return (
        <div className='flex items-center gap-3'>
          <Switch checked={value === true} onCheckedChange={(checked) => onChange(checked)} disabled={disabled} />
          <span className='text-sm'>{value ? 'Yes' : 'No'}</span>
        </div>
      );
    }
    case 'list': {
      const listValue = Array.isArray(value) ? value.join(', ') : '';
      return (
        <Input
          id='new-value'
          type='text'
          value={listValue}
          onChange={(e) =>
            onChange(
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            )
          }
          placeholder='Enter values separated by commas'
          disabled={disabled}
        />
      );
    }
    case 'string':
    default: {
      const stringValues = schema?.values as string[] | undefined;

      if (stringValues && Array.isArray(stringValues)) {
        const options = stringValues.map((val) => ({
          value: val,
          label: val,
        }));

        return (
          <Select
            value={value || ''}
            onChange={(val) => onChange(val)}
            options={options}
            placeholder='Select a value'
            disabled={disabled}
            className='w-full'
          />
        );
      }

      return (
        <Input
          id='new-value'
          type='text'
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder='Enter text'
          disabled={disabled}
        />
      );
    }
  }
}
